/**
 * Host-aware router (T9.7: manifest-driven module routes).
 *
 * The same Vite bundle is served from two CloudFront distributions:
 *
 *   • app.cuidari.com / ariadne.francisquini.ai (legacy)
 *     → product surface (finance app for Cuidari users)
 *   • console.francisquini.ai
 *     → internal admin surface (Peitho, Kratos, Odysseus, Thalia,
 *       Eunomia, Admin)
 *
 * We bifurcate the `routeTree` so each host only registers the routes it
 * is allowed to serve. Unknown paths fall through to a contextual 404
 * (see `@/modules/console/NotFound`). The previous "redirect everything
 * to `/`" behaviour leaked admin URLs into the product surface; the
 * split is what stops an authenticated Cuidari user from stumbling onto
 * `/peitho` and finding the door open.
 *
 * Note: this filter happens at *router* construction. The defence-in-depth
 * RBAC (`<RequireAdmin>`) is still applied per page — see
 * `@/modules/admin/RequireAdmin` for the strict super_admin policy on
 * the admin host.
 *
 * T9.7 — module pages are no longer declared inline. Each module's
 * `manifest.ts` owns its `path` + `component` + optional
 * `validateSearch`; `buildModuleRoutes(rootRoute, surface)` (in
 * `@/lib/modules/routeBuilder`) walks the eagerly-imported manifest
 * registry and emits a TanStack route per `ModuleRoute`. Adding a new
 * domain now requires *zero* changes to this file — the macro
 * acceptance test ("2nd domain spike") is fully covered.
 *
 * Static routes that remain hand-written here are framework /
 * shell-level concerns, not module concerns:
 *   - legacy product redirects (`/`, `/bills`, `/insights`, `/historico`)
 *   - auth surfaces (`/login`, `/entrar`, `/verify`, `/reset`) — note there
 *     is no standalone `/signup` route: signup happens inside `/entrar`
 *     (invite redemption) and the waitlist join lives inside `/login`.
 *   - the console host root routes (`/` → /cockpit, `/login` → ConsoleLogin)
 *   - the catch-all `$` 404 sentinel
 *
 * TODO(host-aware bundle): Vite still bundles every page on both hosts.
 * A follow-up should introduce a build-time `VITE_HOST_KIND` env var
 * and emit two separate artifacts, one per origin. The runtime check
 * we have today only fixes surface leakage, not bundle weight.
 */
import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
} from '@tanstack/react-router'
import { RootLayout } from '@/routes/__root'
import { LoginPage } from '@/routes/login-gate/LoginPage'
import { ResetPage } from '@/routes/reset'
import { EntrarPage } from '@/routes/entrar'
import { VerifyPage } from '@/routes/verify'
import { InstalarPage } from '@/routes/instalar'
import { SharePage } from '@/routes/share'
import { ConsoleHome, ConsoleLogin, NotFound } from '@/modules/console'
import { getHostKind, type HostKind } from '@/lib/host'
import { buildModuleRoutes } from '@/lib/modules/routeBuilder'
import { CuidariRouteError } from '@/components/shared/CuidariRouteError'
import { IndexPage } from '@/routes/index'

const str = (v: unknown) => (typeof v === 'string' ? v : undefined)

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: CuidariRouteError,
})

// ─── Legacy product redirects (kept static, see T9 F3.5) ─────────────────────
//
// These four routes (`/`, `/bills`, `/insights`, `/historico`) only exist
// to forward externally-emitted deep links (Pheme/Iris notifications,
// marketing emails) to the canonical `/finance/*` paths. They cannot live
// in a manifest because manifests describe what a module *owns*; these
// are routing-shell concerns. F3.7 deletes them after the 30-day bake
// window. Search params travel with the redirect (`search: (prev) => prev`)
// so a hit to `/bills?open_bill=abc&action=mark_paid` lands on
// `/finance/instances?open_bill=abc&action=mark_paid`.

// `/` no host de produto serve o gate de instalação (`IndexPage`). Até
// 2026-08-24 renderizava `modules/finance/HomePage`, que para o usuário
// autenticado em desktop caía em `<DesktopUnderConstruction />` — um beco
// sem saída. Com a SPA consumer deletada, o app Cuidari vive no Expo em
// `/m/*`; em `app.cuidari.com` o CloudFront já redireciona a raiz antes de
// chegar aqui, então esta rota atende `ariadne.francisquini.ai` e o preview.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (raw: Record<string, unknown>): {
    highlight_bin?: string; open_bill?: string; highlight_alert?: string; source?: string
  } => ({
    highlight_bin:   str(raw.highlight_bin),
    open_bill:       str(raw.open_bill),
    highlight_alert: str(raw.highlight_alert),
    source:          str(raw.source),
  }),
  component: IndexPage,
})


// ─── Auth routes (kept static) ───────────────────────────────────────────────
//
// `/login` and `/entrar` live in routes/ (not modules/) because they
// pre-date the module pattern and have their own bootstrap concerns
// (Cerberus session, redemption tokens). There is no standalone `/signup`
// route — signup is performed inside `/entrar` from the invite token.
// Hoisting these into a manifest would gain nothing — there is no "auth
// module" to scaffold next.

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})


// `/reset` — Cerberus password-reset confirmation page (product-agnostic).
// Token + user_id arrive in the query string from the reset email Cerberus
// sent using the calling product's `return_to`. Anyone with a valid token
// must be able to land here without being authed, hence the public path
// registration in `__root.tsx`.
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset',
  component: ResetPage,
  validateSearch: (raw: Record<string, unknown>): { token?: string; user_id?: string } => ({
    token:   str(raw.token),
    user_id: str(raw.user_id),
  }),
})

// T5 — Waitlist invite redemption (`/entrar?invite=<token>&entry=<id>`).
const entrarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  component: EntrarPage,
  validateSearch: (raw: Record<string, unknown>): { invite?: string; entry?: string } => ({
    invite: str(raw.invite),
    entry:  str(raw.entry),
  }),
})

// Email-verification landing — Cerberus signup emails link here via
// `return_to` instead of the canonical IdP. Public-path; mirrors the
// `/reset` and `/entrar` posture.
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  component: VerifyPage,
  validateSearch: (raw: Record<string, unknown>): { token?: string } => ({
    token: str(raw.token),
  }),
})

// ─── Phase 8 (M8) public mobile-web-minimal surfaces ────────────────────────
//
// These three routes are the "porta de entrada" Cuidari opens to people
// arriving without an account: install page, share-card preview, and
// the landing — see `__root.tsx` + `cerberus/AuthGate.tsx` for the
// matching PUBLIC_PATHS entries. They are intentionally NOT module
// routes because each is a single page with no module ecosystem.

const instalarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/instalar',
  component: InstalarPage,
})

const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: SharePage,
})

// ─── Console host shell routes (kept static) ─────────────────────────────────
//
// Console root + console login share their paths with the product `/` and
// `/login` and therefore cannot coexist in the same manifest tree. They
// are intentionally registered as plain shell routes (no module).

const consoleHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/cockpit', replace: true })
  },
  component: ConsoleHome,
})

const consoleLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: ConsoleLogin,
})

// ─── Catch-all 404 (host-aware copy) ─────────────────────────────────────────

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  component: NotFound,
})

// The product tree historically redirected unknown paths to `/`. We now
// surface a real 404 instead so admin routes (e.g. `/peitho`) accessed on
// the product host produce a clearly-not-found page rather than silently
// landing the user on Cuidari home (which masked the leak).

// ─── Module routes (manifest-driven, T9.7) ───────────────────────────────────
//
// Every page that belongs to a module (`src/modules/<name>/manifest.ts`)
// is registered through the builder. The `surface` field on each manifest
// decides which host gets the route. Adding a new domain = drop in a new
// `manifest.ts` + page files; this file does not change.

const productModuleRoutes = buildModuleRoutes(rootRoute, 'product')
const adminModuleRoutes = buildModuleRoutes(rootRoute, 'admin')

// ─── Per-host route tree assembly ────────────────────────────────────────────

const PRODUCT_STATIC_ROUTES = [
  indexRoute,
  loginRoute,
  resetRoute,
  entrarRoute,
  verifyRoute,
  instalarRoute,
  shareRoute,
] as const

const ADMIN_STATIC_ROUTES = [consoleHomeRoute, consoleLoginRoute] as const

/**
 * Pure helper exported for tests. Given a host kind, returns the array
 * of routes the router should register. `unknown` hosts are treated as
 * product (avoids breaking previewers, dev tunnels, or curl probes).
 */
export function pickRoutesForHost(kind: HostKind) {
  if (kind === 'admin')
    return [...ADMIN_STATIC_ROUTES, ...adminModuleRoutes, notFoundRoute]
  return [...PRODUCT_STATIC_ROUTES, ...productModuleRoutes, notFoundRoute]
}

/**
 * Type-only route tree containing every product + admin route. Used to
 * widen the `Register['router']` type so that `<Link to="/profile" />` and
 * `navigate({ to: '/peitho' })` type-check in any source file regardless
 * of which host actually mounts those routes.
 *
 * The tree is built from `TYPE_ONLY_MODULE_SPECS` (one entry per manifest,
 * see `src/lib/modules/typeOnlyRouteSpecs.ts`) — a type-only mirror of
 * the manifest registry that preserves literal `path` strings TS lost
 * when `buildModuleRoutes()` iterated the runtime values. Adding a new
 * module appends one entry to `typeOnlyRouteSpecs.ts`; this file does
 * not change.
 *
 * The admin login route is intentionally omitted from this union — it
 * shares `/login` with the product LoginPage and TanStack rejects
 * duplicate paths in the same tree (the runtime split below picks one
 * or the other).
 */
import { typeOnlyModuleRoutes } from '@/lib/modules/typeOnlyRouteSpecs'
const TYPE_ONLY_ROUTES = [
  ...PRODUCT_STATIC_ROUTES,
  ...typeOnlyModuleRoutes(rootRoute, 'product'),
  ...ADMIN_STATIC_ROUTES.filter(
    (r) => r !== consoleHomeRoute && r !== consoleLoginRoute,
  ),
  ...typeOnlyModuleRoutes(rootRoute, 'admin'),
  notFoundRoute,
] as const

// `typeOnlyTree` is intentionally retained only for its inferred type
// (see the cast on `routeTree` below). ESLint flags it as unused because
// the value is never referenced; the type IS referenced via `typeof`.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typeOnlyTree = rootRoute.addChildren(TYPE_ONLY_ROUTES)

// Runtime tree: only the routes the current host should serve. Cast to
// the wide type so callers everywhere can `to: '/peitho'` even on the
// product host (where it resolves to a 404 at runtime).
export const routeTree = rootRoute.addChildren(
  pickRoutesForHost(getHostKind()),
) as unknown as typeof typeOnlyTree

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
